import { UserStore } from '../../engine/streamrapp/UserStore';
import { BroadcastStateService } from '../broadcaststate/BroadcastStateService';
import { MediaService } from '../media/MediaService';
import { SceneService } from '../scene/SceneService';
import { StreamrAppService } from '../streamrapp/StreamrAppService';
import { AcceptedParticipantType } from '../user/UserServiceOutput';
import { getCaptureErrorMessage, parseShareParams } from './parseShareParams';
import { ShareLinkServiceOutput } from './ShareLinkServiceOutput';
import { ShareLinkStore } from './ShareLinkStore';
/**
 * Orchestrates screen-share deep-link sessions (the /share page, aimed at AI
 * agents driving a browser): one user gesture starts screen capture, then a
 * new broadcast session is created and auto-activated on the session page.
 *
 * Invariants for these sessions:
 * - The camera/microphone are never captured (MediaService 'screen-only'
 *   capture policy — also re-applied on reload via the persisted marker).
 * - When the screen share ends, by any path, the broadcast ends; there is
 *   no camera fallback.
 */
export class ShareLinkService {
    static instance;
    static getInstance() {
        if (!ShareLinkService.instance) {
            ShareLinkService.instance = new ShareLinkService();
        }
        return ShareLinkService.instance;
    }
    static getNotificationInterface() {
        return ShareLinkService.getInstance();
    }
    static getOutput() {
        return ShareLinkServiceOutput.getReactiveOutput();
    }
    // ------------- Singleton implementation end
    output;
    store;
    constructor() {
        this.output = new ShareLinkServiceOutput();
        this.store = new ShareLinkStore();
    }
    async init() {
        MediaService.getInstance().on('screenShareEnded', () => {
            this.onScreenShareEnded();
        });
        BroadcastStateService.getInstance().on('activeBroadcastChanged', (broadcast) => {
            this.onActiveBroadcastChanged(broadcast);
        });
    }
    /**
     * A share-link session is (re)entering — typically a reload of the
     * session page. Re-applies the screen-only policy before any capture can
     * start, and cancels auto-resume when the capture did not survive the
     * reload (a resumed broadcast would be black and would otherwise prompt
     * for camera/microphone).
     */
    onActiveBroadcastChanged(broadcast) {
        if (broadcast === undefined) {
            return;
        }
        if (!this.store.hasShareSession(broadcast.streamId)) {
            return;
        }
        MediaService.getInstance().setCapturePolicy('screen-only');
        if (!MediaService.getInstance().isScreenShareActive()) {
            new UserStore().setHostWasLive(broadcast.streamId, false);
        }
    }
    onScreenShareEnded() {
        const activeBroadcast = BroadcastStateService.getInstance().getActiveBroadcast();
        if (activeBroadcast === undefined) {
            return;
        }
        if (!this.store.hasShareSession(activeBroadcast.streamId)) {
            return;
        }
        this.store.clearShareSession(activeBroadcast.streamId);
        new UserStore().setHostWasLive(activeBroadcast.streamId, false);
        StreamrAppService.getInstance().notifyDeactivateBroadcast();
    }
    /**
     * The /share page's single-click entry point. Must be called from a user
     * gesture (getDisplayMedia requires transient activation). Captures the
     * screen first — a dismissed picker leaves no session behind — then
     * creates the session and hands the broadcast path to onGoingLive for
     * SPA navigation; the session page auto-activates the broadcast through
     * the host-was-live recovery hook.
     */
    notifyStartShare(rawParams, onGoingLive) {
        this.startShare(rawParams, onGoingLive).catch((e) => {
            console.error('[ShareLinkService] starting share failed', e);
            this.output.setError(e instanceof Error ? e.message : 'Starting the share failed');
        });
    }
    async startShare(rawParams, onGoingLive) {
        const params = parseShareParams(rawParams);
        const userStore = new UserStore();
        if (params.name !== undefined) {
            userStore.setDisplayName(params.name);
        }
        else if (!userStore.getDisplayName()) {
            userStore.setDisplayName('Agent');
        }
        MediaService.getInstance().setCapturePolicy('screen-only');
        this.output.setPhase('capturing');
        try {
            await MediaService.getInstance().requestScreenShare(params.displaySurface, { audio: params.includeAudio });
        }
        catch (e) {
            console.warn('[ShareLinkService] screen capture not started', e);
            this.output.setError(getCaptureErrorMessage(e));
            return;
        }
        this.output.setPhase('starting');
        let broadcastPath;
        let streamId;
        StreamrAppService.getInstance().notifyActivateSession(undefined, 'broadcast', {
            name: params.title,
            // Called synchronously by notifyActivateSession
            onActivate: (path, session) => {
                broadcastPath = path;
                streamId = session.streamId;
            },
        });
        if (broadcastPath === undefined || streamId === undefined) {
            this.output.setError('Creating the session failed');
            return;
        }
        // Share-link sessions are watch-only for the audience: with
        // AcceptedParticipantType.NONE in the broadcast metadata, viewers
        // get no "Request to speak/join" UI. Written to the store directly
        // too because StreamrAppService's currentStreamId is not set until
        // rendering is prepared on the session page.
        StreamrAppService.getInstance().notifySetAcceptedParticipantType(AcceptedParticipantType.NONE);
        new UserStore().setAcceptedParticipantType(streamId, AcceptedParticipantType.NONE);
        const displayScene = SceneService.getInstance().getDisplayScene();
        if (displayScene) {
            SceneService.getNotificationInterface().notifySetLiveScene(displayScene.id);
        }
        // Seed the auto-activation the session page's lobby performs for a
        // host that "was live" — this is what goes live without a click.
        userStore.setHostWasLive(streamId, true);
        this.store.setShareSession(streamId);
        onGoingLive(broadcastPath);
    }
}
